import React from 'react';
import noop from 'lodash/noop';
import cn from 'classnames';

import Tooltip from 'admin/components/common/Tooltip';
import { EventEmitter } from 'src/common/utils/event-emitter';
import css from './BoxShadowSettings.scss';

export const OFFSET_MAX = 20;

interface Vector2 {
	x: number;
	y: number;
}

interface Props {
	className?: string;
	offsetX: number;
	offsetY: number;
	eventEmitter: EventEmitter<{ type: 'complete' }>;
	onUpdate: (x: number, y: number) => void;
}

export const OffsetControl: React.FC<Props> = props => {
	const { offsetX, offsetY, eventEmitter, className = '', onUpdate } = props;
	const [isMousePressed, setIsMousePressed] = React.useState(false);
	const [isMouseLeft, setIsMouseLeft] = React.useState(true);
	const [containerBBox, setContainerBBox] = React.useState<DOMRect | null>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const dndControllerRef = React.useRef<HTMLDivElement>(null);
	const isTooltipOpen = isMousePressed || !isMouseLeft;
	const controllerPoint = getPointInsideRect(
		{
			x: offsetX * (containerBBox ? containerBBox.width / 2 : 1),
			y: offsetY * (containerBBox ? containerBBox.height / 2 : 1),
		},
		containerBBox
	);

	React.useLayoutEffect(() => {
		const { current: container } = containerRef;
		if (!container) return;

		setContainerBBox(container.getBoundingClientRect());
	}, []);

	React.useEffect(() => {
		const { current: container } = containerRef;
		const { current: dndController } = dndControllerRef;
		if (!container || !dndController || !containerBBox) return noop;

		const abortController = new AbortController();
		const { width: w, height: h } = containerBBox;

		const onUpdateByPercentage = (x: number, y: number) => {
			onUpdate(x / (w / 2), y / (h / 2));
		};

		const handleMouseEvent = (event: MouseEvent) => {
			event.preventDefault();

			switch (event.type) {
				case 'mouseenter': {
					setIsMouseLeft(false);
					break;
				}
				case 'mousedown': {
					const v = centralVector(event.clientX, event.clientY, container.getBoundingClientRect());

					onUpdateByPercentage(v.x, v.y);

					document.addEventListener('mouseup', handleMouseEvent, { signal: abortController.signal });
					document.addEventListener('mousemove', handleMouseEvent, { signal: abortController.signal });

					setIsMouseLeft(false);
					setIsMousePressed(true);
					break;
				}

				case 'mousemove': {
					const { clientX: x, clientY: y } = event;
					const rect = container.getBoundingClientRect();
					const v = centralVector(x, y, rect);
					const p = getPointInsideRect(v, rect);

					onUpdateByPercentage(p.x * (rect.width / 2), p.y * (rect.height / 2));
					break;
				}

				case 'mouseup': {
					eventEmitter.emit('complete');
					setIsMousePressed(false);

					document.removeEventListener('mousemove', handleMouseEvent);
					document.removeEventListener('mouseup', handleMouseEvent);
					break;
				}

				case 'mouseleave': {
					setIsMouseLeft(true);
					break;
				}

				default:
					break;
			}
		};

		container.addEventListener('mousedown', handleMouseEvent, { signal: abortController.signal });
		dndController.addEventListener('mouseenter', handleMouseEvent, { signal: abortController.signal });
		dndController.addEventListener('mouseleave', handleMouseEvent, { signal: abortController.signal });

		return () => {
			abortController.abort();
		};
	}, [containerBBox, eventEmitter, onUpdate]);

	return (
		<div className={cn(css.offsetControl, className)} ref={containerRef}>
			<Tooltip
				isStatic
				stylePreset="dark-2"
				content={`${Math.round(offsetX * OFFSET_MAX)}, ${Math.round(offsetY * OFFSET_MAX)}`}
				placement="top"
				open={isTooltipOpen}
			>
				<div
					className={css.dndController}
					style={{
						left: `${(controllerPoint.x / 2 + 0.5) * 100}%`,
						top: `${(controllerPoint.y / 2 + 0.5) * 100}%`,
					}}
					ref={dndControllerRef}
				/>
			</Tooltip>
		</div>
	);
};

const centralVector = (x: number, y: number, rect: DOMRect): Vector2 => {
	const { top, left, width, height } = rect;

	return {
		x: x - left - width / 2,
		y: y - top - height / 2,
	};
};

const magnitude = (vector: Vector2) => {
	return Math.sqrt(vector.x * vector.x + vector.y * vector.y);
};

const normalize = (vector: Vector2) => {
	const mag = magnitude(vector);
	if (mag === 0) {
		return { x: 0, y: 0 };
	}
	return {
		x: vector.x / mag,
		y: vector.y / mag,
	};
};

const intersection = (p1: Vector2, uv1: Vector2, p2: Vector2, uv2: Vector2) => {
	const det = uv1.x * uv2.y - uv1.y * uv2.x;

	if (det === 0) {
		return null;
	}

	const t = ((p2.x - p1.x) * uv2.y - (p2.y - p1.y) * uv2.x) / det;

	return {
		x: p1.x + t * uv1.x,
		y: p1.y + t * uv1.y,
	};
};

const vectorAngle = (v: Vector2) => {
	const rad = 180 / Math.PI;
	return (Math.atan2(v.y, v.x) * rad + 360 + 90) % 360;
};

const getPointInsideRect = (v: Vector2, rect: DOMRect | null) => {
	if (!rect) {
		return { x: 0, y: 0 };
	}

	const { width: w, height: h } = rect;
	const uv = normalize(v);
	const angle = vectorAngle(v);
	const center = { x: 0, y: 0 };

	const topIntersection = intersection({ x: -w / 2, y: -h / 2 }, { x: 1, y: 0 }, center, uv);
	const rightIntersection = intersection({ x: w / 2, y: -h / 2 }, { x: 0, y: 1 }, center, uv);
	const bottomIntersection = intersection({ x: w / 2, y: h / 2 }, { x: -1, y: 0 }, center, uv);
	const leftIntersection = intersection({ x: -w / 2, y: w / 2 }, { x: 0, y: -1 }, center, uv);

	let intersectionVector: Vector2 | null = null;

	if (angle >= 45 && angle < 135 && rightIntersection) {
		intersectionVector = rightIntersection;
	} else if (angle >= 135 && angle < 225 && bottomIntersection) {
		intersectionVector = bottomIntersection;
	} else if (angle >= 225 && angle < 315 && leftIntersection) {
		intersectionVector = leftIntersection;
	} else if ((angle >= 315 || angle < 45) && topIntersection) {
		intersectionVector = topIntersection;
	}

	let result: Vector2 = v;
	if (intersectionVector) {
		result = magnitude(v) < magnitude(intersectionVector) ? v : intersectionVector;
	}

	return {
		x: result.x / (w / 2),
		y: result.y / (h / 2),
	};
};

export default OffsetControl;
